/**
 * Route-level error element for the data router.
 *
 * React Router's `createBrowserRouter` catches errors thrown during route
 * rendering *before* they propagate to the React `ErrorBoundary` tree wrapping
 * `<RouterProvider>`. Without an explicit `errorElement` the user sees React
 * Router's default "Hey developer" debug screen — which is exactly what
 * happened when a clinician hit a stale-chunk error mid-task.
 *
 * This component covers both shapes of error that can reach the route level:
 *
 *   1. Stale-chunk errors (user on an old tab after a redeploy). We show a
 *      friendly "Update available" screen and offer a hard reload.
 *      `lazyWithRetry` normally auto-reloads before we get here; this UI is
 *      the last-resort fallback when the reload loop guard tripped.
 *
 *   2. Everything else (genuine application bugs). We show a generic
 *      fallback and report the error to Datadog.
 *
 * @agentic connections
 */

import { useEffect } from "react";
import { Box, Button, Stack, Text } from "@mantine/core";
import { isRouteErrorResponse, useRouteError } from "react-router";
import { reportError } from "@/app/boot/initializeDatadog";
import { dismissBootSplash } from "@/app/boot/bootSplash";
import { isChunkLoadError } from "@/utils/ui/lazyWithRetry";

/** Force a hard reload that bypasses the HTTP cache. @agentic connections */
const hardReload = () => {
  if (typeof window !== "undefined") window.location.reload();
};

/**
 * Fallback UI rendered when a route throws. Distinguishes stale-chunk errors
 * from generic application errors.
 * @agentic connections
 */
export const RouteErrorBoundary = () => {
  const error = useRouteError();
  const isStaleChunk = isChunkLoadError(error);

  // A route-level error renders here instead of SignInPage/AuthenticatedLayout,
  // which are what normally dismiss the static boot splash — so dismiss it here
  // too, otherwise the full-screen overlay stays on top and blocks these
  // recovery actions.
  useEffect(() => {
    dismissBootSplash();
  }, []);

  useEffect(() => {
    if (isRouteErrorResponse(error)) {
      reportError(
        new Error(`Route error ${error.status}: ${error.statusText}`),
        { source: "RouteErrorBoundary", status: error.status },
      );
      return;
    }

    reportError(error, {
      source: "RouteErrorBoundary",
      isStaleChunk,
    });
  }, [error, isStaleChunk]);

  if (isStaleChunk) {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          padding: "2rem",
        }}
      >
        <Stack align="center" gap="sm" maw={420}>
          <Text fw={600} size="lg">
            A newer version is available
          </Text>
          <Text c="dimmed" size="sm" ta="center">
            This tab is running an older version of the app and the page you
            tried to open is no longer on the server. Refresh to load the latest
            version.
          </Text>
          <Button onClick={hardReload} mt="sm">
            Refresh now
          </Button>
        </Stack>
      </Box>
    );
  }

  const message =
    error instanceof Error
      ? error.message
      : isRouteErrorResponse(error)
        ? `${error.status} ${error.statusText}`
        : "An unexpected error occurred.";

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        padding: "2rem",
      }}
    >
      <Stack align="center" gap="sm" maw={420}>
        <Text fw={600} size="lg">
          Something went wrong
        </Text>
        <Text c="dimmed" size="sm" ta="center">
          The error has been reported and our team will investigate. You can try
          refreshing the page or returning to the previous screen.
        </Text>
        <Text c="red" size="xs" ta="center">
          {message}
        </Text>
        <Button onClick={hardReload} mt="sm" variant="light">
          Refresh
        </Button>
      </Stack>
    </Box>
  );
};
